import {
  Activity,
  EDRResponse,
  EDRResponses,
  ExitCodeGroup,
  PartnerExpected,
  PersonalActivity,
} from "@theprelude/sdk";

type ODPFields = {
  prevented: number;
  detected: number;
  observed: number;
};

export type OptionalODPFields = Partial<ODPFields>;

export function getPersonalPercentages(
  personalActivity?: PersonalActivity,
): OptionalODPFields {
  if (!personalActivity) {
    return {};
  }

  const preludeTotal = personalActivity.prelude_total;
  const partnerTotal = personalActivity.partner_total;
  const prevented = personalActivity.prevented;
  const detected = personalActivity.detected;
  const observed = personalActivity.observed;

  if (partnerTotal === 0 && preludeTotal === 0) {
    return {};
  }

  return {
    prevented: Math.round((prevented / preludeTotal) * 100),
    detected:
      partnerTotal === 0 ? 0 : Math.round((detected / partnerTotal) * 100),
    observed:
      partnerTotal === 0 ? 0 : Math.round((observed / partnerTotal) * 100),
  };
}

export function getProtectedPercentage(personalActivity: PersonalActivity) {
  return getPersonalPercentages(personalActivity)?.prevented;
}

export function isActivityPresent(activity?: PersonalActivity) {
  return activity && activity?.prelude_total > 0;
}

export type ExpectedName = "observed" | "detected" | "prevented";

export function getExpectedName(
  expectedResponse: EDRResponse | null | undefined,
): ExpectedName | undefined {
  if (!expectedResponse) {
    return undefined;
  }
  if (expectedResponse === EDRResponses.OBSERVE) {
    return "observed";
  }
  if (expectedResponse === EDRResponses.DETECT) {
    return "detected";
  }
  if (expectedResponse === EDRResponses.PREVENT) {
    return "prevented";
  }
}

export function getExpectedPercent(
  personalActivity?: PersonalActivity,
  expectation?: "observed" | "detected" | "prevented",
) {
  if (!personalActivity) {
    return;
  }
  if (expectation === "observed" || expectation === "detected") {
    const numerator = personalActivity[expectation];
    if (numerator === 0) return 0;
    return Math.round((numerator / personalActivity.partner_total) * 100);
  }

  if (personalActivity.prelude_total === 0) {
    return;
  }

  return Math.round(
    (personalActivity.prevented / personalActivity.prelude_total) * 100,
  );
}

export type ExpectedResult = {
  expectation?: ExpectedName;
  percent?: number;
};

export function getExpectedResult(
  expected?: PartnerExpected,
  personal?: PersonalActivity,
) {
  if (!expected || !expected?.crowdstrike) {
    // TODO: Check if crowdstrike is enabled instead of just checking if it exists
    return;
  }

  const expectation = getExpectedName(expected.crowdstrike);
  const percent = getExpectedPercent(personal, expectation);

  return {
    expectation,
    percent,
  };
}

export interface ExpectedOutcome {
  expectation: ExpectedName;
  isExpected: "yes" | "no" | "pending";
}

export function getExpectationStatus(
  activity: Activity,
  expectation?: ExpectedName,
): "yes" | "no" | "pending" {
  if (!expectation) {
    return "no";
  }
  const isPass = (ExitCodeGroup.PROTECTED as unknown as number[]).includes(
    activity.status,
  );
  if (expectation === "detected")
    return activity.detected === "pending"
      ? "pending"
      : activity.detected
        ? "yes"
        : "no";
  if (expectation === "observed")
    return activity.observed === "pending"
      ? "pending"
      : activity.observed
        ? "yes"
        : "no";
  if (expectation === "prevented") return isPass ? "yes" : "no";

  return "no";
}
